import { useMemo } from 'react';
import {
  closeMonthOf,
  computeStageBridge,
  computeStrandedCapacity,
  computeStrandedTimeline,
  computeTsaEconomics,
  day1Label,
  fmt,
  fmtMoney,
  metricFn,
  monthLabel,
  summarySizeRows,
  type MetricKey,
  type SeparationState,
} from '@integrate/core';
import { MetricToggle } from './MetricToggle.js';
import { OneOffTable } from './OneOffTable.js';
import { PopulationSankey } from './PopulationSankey.js';
import { StatTile } from '../ui/StatTile.js';

/*
  The separation summary: the whole design on one page.

  Every figure here is an existing, parity-covered engine — this pane adds no arithmetic of its own, and
  deliberately so. Where the app computed something inline in its template, that expression is the thing
  most likely to disagree with the same number elsewhere, so the two that mattered are settled rather
  than reproduced:

  ORG COSTS ARE RAW SALARY, economics are loaded. The page says so, and now means it: the bridge weight
  comes from `metricFn('cost', state.assumptions)`, which is raw salary and is asserted to be, while `costMetric` — the
  loaded weight — stays with the TSA and stranded figures below. The deck printed the loaded one under
  this same sentence until P4l-a.

  THE HEADLINE STRANDED FIGURE has two spellings in core — `computeStrandedCapacity(...).tot.strandedCost`
  and `computeStrandedTimeline(...).grossRunRate` — which are the same quantity by two paths. The stranded
  tab uses the first, so this uses the first, and a core test pins them equal. Two tabs of one modal
  disagreeing about the headline number is the failure this avoids.

  THE SANKEY IS HERE NOW, and on the terms the earlier refusal set: the objection was that a diagram
  which has to be right about proportions is worse than absent while it is approximate, so
  `PopulationSankey` is not an approximation — it ports the frozen app's own geometry intact and draws
  `separationSankeyFlows`, whose every ribbon is derived from the same `computeStageBridge` result the
  tables on this page print. The picture and the tables cannot disagree, because they are one result.
*/

export function SummaryPane({
  state,
  scenarioName,
  sankeyMetric,
  onSankeyMetric,
  onOpenAssumptions,
}: {
  state: SeparationState;
  scenarioName: string;
  /** The modal's, like every pane control: pane-local state dies with a tab switch. */
  sankeyMetric: MetricKey;
  onSankeyMetric(next: MetricKey): void;
  onOpenAssumptions(): void;
}): JSX.Element {
  const B = useMemo(
    () => ({
      // `metricFn` is core's single map from a toggle key to a weight — the one place the three-way
      // choice is made, so two panes cannot disagree about what "Cost" means.
      hc: computeStageBridge(state, { fn: metricFn('hc', state.assumptions) }),
      fte: computeStageBridge(state, { fn: metricFn('fte', state.assumptions) }),
      cost: computeStageBridge(state, { fn: metricFn('cost', state.assumptions) }),
    }),
    [state],
  );
  const E = useMemo(() => computeTsaEconomics(state), [state]);
  const T = useMemo(() => computeStrandedTimeline(state), [state]);
  const C = useMemo(() => computeStrandedCapacity(state, {}), [state]);

  /*
    Whether anybody carries stranded capacity at all — asked of the population this block PRICES.

    The app asks `state.employees.some(e => e._dedication != null)`: no headcount filter, no build-ups.
    So a project where only inactive people or attrition leavers carry a dedication figure rendered the
    whole block with all-zero carriers, which reads as "measured, and it is nothing" rather than "nobody
    here is measured".
  */
  const anyCarrier = C.tot.strandedN > 0 || T.carriers.length > 0;

  /*
    In core, so the harness can hold it. Five of these rows are a field read and the sixth is a
    subtraction the app performs inside its template — the RemainCo steady state a steering committee
    reads off the page, computed in the one place no test can reach.

    The "existing people" caption is that function's `existingOnly` flag, not a note invented here: the
    bridge adds no Remain-side build-up, so the RemainCo sizes exclude exactly the hires the one-off
    table below charges for. The node card's stage line already says this in the same words.
  */
  const rows = useMemo(() => summarySizeRows(B), [B]);

  /*
    `n` is carried beside the formatted string so the bridge identity is checkable from the rendered
    page: `day1 = perimeter + transfers + d1BuildUps` spans this block and the table above it, and a
    sign glyph and a thousands separator are not something a test should be parsing back out.
  */
  const flows: { key: string; label: string; n: number; d1?: number; value: string; sub: string }[] = [
    {
      key: 'transfers',
      label: 'Day 1 transfers',
      n: B.hc.transfers,
      value: `+${fmt(B.hc.transfers)}`,
      sub: 'shared people joining at Day 1',
    },
    {
      key: 'buildups',
      label: 'Carve-out build-ups',
      n: B.hc.d1BuildUps + B.hc.tsaRepl + B.hc.d2BuildUps,
      // The Day 1 share, which the sub-line below already states in words.
      d1: B.hc.d1BuildUps,
      value: `+${fmt(B.hc.d1BuildUps + B.hc.tsaRepl + B.hc.d2BuildUps)}`,
      // Named for what it counts. The app calls this "Build-ups" while summing only the carve-out ones,
      // on the page whose one-off table prices RemainCo hires beside it.
      sub: `${fmt(B.hc.d1BuildUps)} Day 1 · ${fmt(B.hc.tsaRepl)} TSA repl. · ${fmt(B.hc.d2BuildUps)} Day 2`,
    },
    {
      /*
        Positions, not people — and the caption says which, because the one-off table below counts the
        other thing. `rightsizing` includes a Day 1 build-up cancelled at Day 2, which has no incumbent
        to sever, so its severance row is employees only (`O.d2N`). Four employees plus one cancelled
        hire is a tile reading −5 above a row reading 4, and core states the rule where nothing on screen
        did. This is the same fix as `existingOnly` on the RemainCo sizes, in the same milestone.
      */
      key: 'day2-exits',
      label: 'Day 2 exits',
      n: B.hc.rightsizing,
      value: `−${fmt(B.hc.rightsizing)}`,
      sub: 'carve-out positions leaving after Day 1, cancelled hires included',
    },
    { key: 'nlr', label: 'No longer required', n: B.hc.nlr, value: fmt(B.hc.nlr), sub: 'redundant at close' },
    {
      key: 'remain-rightsize',
      label: 'Stranded rightsizings',
      n: B.hc.remainRightsize,
      value: `−${fmt(B.hc.remainRightsize)}`,
      sub: 'RemainCo, per the stranded plan',
    },
  ];

  return (
    <div data-testid="summary-pane">
      <p className="muted">
        Scenario <strong>{scenarioName}</strong> — the whole separation design on one page:
        organisation sizes, people flows, TSA economics, stranded cost and one-off costs. Same
        population rules as the phase bridge (filled people, attrition leavers excluded); org costs are
        raw salaries, economics are loaded, and severance is base salary.
        {day1Label(state.assumptions) !== null && (
          <>
            {' '}
            Day 1 = <strong data-testid="summary-day1">{day1Label(state.assumptions)}</strong>.
          </>
        )}
        {(() => {
          // Deferred closings on the one-page summary: named only when a carve-out member's
          // country actually closes late, so every simultaneous-close project reads as before.
          const late = new Map<string, number>();
          for (const e of state.employees) {
            if (e.isLeaver || e.openPosition || e.inactive) continue;
            if (state.design.buckets[e.id] !== 'Carve-out') continue;
            const month = closeMonthOf(e, state.assumptions);
            if (month > 0) late.set(String(e.country), month);
          }
          if (late.size === 0) return null;
          const parts = [...late.entries()]
            .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
            .map(([country, month]) => `${country} ${monthLabel(month, state.assumptions)}`)
            .join(', ');
          return (
            <>
              {' '}
              Deferred closings: <strong data-testid="summary-deferred">{parts}</strong>.
            </>
          );
        })()}
      </p>

      {/* The hero: the one picture of the whole flow, above the tables that itemise it. */}
      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <h4 style={{ margin: 0 }}>Population flow</h4>
        <MetricToggle
          value={sankeyMetric}
          onChange={onSankeyMetric}
          weight="raw"
          testIdPrefix="sankey-metric"
          label=""
        />
      </div>
      <PopulationSankey bridge={B[sankeyMetric]} metric={sankeyMetric} />

      <h4>Organisation sizes</h4>
      <table className="data-table" data-testid="summary-sizes">
        <thead>
          <tr>
            <th>Stage</th>
            <th className="num">Headcount</th>
            <th className="num">FTE</th>
            <th className="num">Cost (raw)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              data-testid={`summary-row-${r.key}`}
              data-stage={r.key}
              data-hc={r.hc}
              data-fte={r.fte}
              data-cost={r.cost}
              style={r.strong ? { fontWeight: 700 } : undefined}
            >
              <td>
                {r.label}
                {r.existingOnly && <span className="muted small"> — existing people</span>}
              </td>
              <td className="num">{fmt(r.hc)}</td>
              <td className="num">{fmt(r.fte, 1)}</td>
              <td className="num">{fmtMoney(r.cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>People flows</h4>
      <div className="stat-grid" data-testid="summary-flows">
        {flows.map((f) => (
          <StatTile
            key={f.key}
            testId={`summary-flow-${f.key}`}
            attrs={{ 'data-n': f.n, 'data-d1': f.d1 }}
            label={f.label}
            value={f.value}
            sub={f.sub}
          />
        ))}
      </div>

      <h4>TSA economics</h4>
      {state.design.tsas.length === 0 ? (
        <p className="muted" data-testid="summary-no-tsas">
          No TSAs registered.
        </p>
      ) : (
        <div className="stat-grid" data-testid="summary-tsa">
          <StatTile
            testId="summary-tsa-count"
            label="TSAs"
            value={fmt(state.design.tsas.length)}
            sub={E.uncovered ? `${fmt(E.uncovered)} uncovered / late` : 'all covered'}
          />
          <StatTile
            testId="summary-tsa-net-month"
            label="Net fees at Day 1"
            value={fmtMoney(E.netMonthAtDay1)}
            sub="per month (paid − received)"
          />
          <StatTile
            testId="summary-tsa-net"
            label="Cumulative net fees"
            value={fmtMoney(E.feesNet)}
            sub="over the TSA durations"
          />
          <StatTile
            testId="summary-tsa-repl"
            label="Replacement run-rate"
            value={fmtMoney(E.replRunRate)}
            sub="carve-out builds (loaded)"
          />
          {E.remainInternalization > 0 && (
            <StatTile
              testId="summary-tsa-internalization"
              label="RemainCo internalization"
              value={fmtMoney(E.remainInternalization)}
              sub="replaces provided TSAs (loaded)"
            />
          )}
        </div>
      )}

      <h4>RemainCo stranded cost</h4>
      {!anyCarrier ? (
        <p className="muted" data-testid="summary-no-stranded">
          Nobody in this design carries stranded capacity — either no dedication is recorded, or nobody
          who has it is on the RemainCo side.
        </p>
      ) : (
        <div className="stat-grid" data-testid="summary-stranded">
          {/* The stranded tab's own spelling, pinned equal to the timeline's in a core test. */}
          <StatTile
            testId="summary-stranded-gross"
            label="Gross stranded at Day 1"
            value={fmtMoney(C.tot.strandedCost)}
            sub={`${fmt(C.tot.strandedN)} carriers (loaded p.a.)`}
          />
          <StatTile
            testId="summary-stranded-covered"
            label="Offset by TSA income"
            value={fmtMoney(T.tsaIncomeMonthly * 12)}
            /*
              "steps down", which the app said and the port dropped. The month is the LONGEST
              received agreement, not the end of the rate beside it: `tsaIncomeMonthly` is month 1's
              income across every received TSA, so two at €10k/6mo and €5k/24mo read "€180,000 p.a.
              until M24" for an income that is €60,000 p.a. from M7.
            */
            sub={
              T.tsaIncomeMonthly
                ? `p.a. now · steps down as agreements end, last at M${T.tsaIncomeMonths}`
                : 'no received TSAs'
            }
          />
          <StatTile
            testId="summary-stranded-mitigated"
            label="Mitigated by the plan"
            value={fmtMoney(T.grossRunRate - T.residualRunRate)}
            sub={
              <>
                {fmt(T.rightsizedN)} rightsized · {fmt(T.redeployedN)} redeployed
              </>
            }
          />
          <StatTile
            testId="summary-stranded-residual"
            label="Residual steady state"
            value={fmtMoney(T.residualRunRate)}
            danger={T.residualRunRate > 0.004}
            sub={
              <>
                p.a. · {fmt(T.residualN)} unmitigated
              </>
            }
          />
          {/*
            Only when there is one. Removing more capacity than was stranded is a backfill risk, and it
            is the one figure on this page that says the plan may be wrong rather than expensive.
          */}
          {T.overRemovalRunRate > 0.004 && (
            <StatTile
              testId="summary-stranded-over"
              label="⚠ Over-removal"
              value={fmtMoney(T.overRemovalRunRate)}
              sub="backfill risk of the rightsizings"
            />
          )}
        </div>
      )}

      <h4>One-off separation costs (severance + hiring)</h4>
      {/* Shared with the phase bridge, which prints the same table — see OneOffTable. */}
      <OneOffTable state={state} testId="summary-oneoff" />

      <p className="muted small">
        Loaded cost = salary × multiplier; severance = months × monthly base salary; hiring ={' '}
        {fmt(state.assumptions.hiringCostPct)}% of a build-up&apos;s salary —{' '}
        <button type="button" className="linkish" data-testid="summary-asm" onClick={onOpenAssumptions}>
          edit assumptions
        </button>
        . All timelines run in months after Day 1.
      </p>
    </div>
  );
}
